import { FC, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Chip, Divider, Grid, Paper, Typography } from '@mui/material';
import { Receipt } from '@mui/icons-material';

import ErrorMessage from '../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../sharedModules/layout/PageHeader';

import axios from '../../sharedModules/utils/axios.utils';

interface SqlField {
  column: string;
  value: string;
}

interface SpotQuoteSqlLog {
  id: number;
  category: string;
  status: string | null;
  query: string;
  fields: SqlField[] | null;
  created_at_with_time: string;
}

const statusColor = (status: string | null): 'success' | 'error' | 'default' => {
  if (status === 'success') return 'success';
  if (status === 'error') return 'error';
  return 'default';
};

const SpotQuoteDetailPage: FC = () => {
  const { xoiJobId } = useParams<{ xoiJobId: string }>();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [xoiJob, setXoiJob] = useState<any>();

  const fetchXoiJob = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/spot_quotes/xoi_jobs/${xoiJobId}`);
      setXoiJob(response.data);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchXoiJob();
  }, []);

  return (
    <Grid container>
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <LoadingWrapper isLoading={loading}>
          <PageHeader
            titleIcon={<Receipt />}
            mainTitle={xoiJob?.customer_name}
            subTitle={xoiJob?.work_order_number}
            backButtonLabel="Spot Quotes"
            backButtonLink="/spot_quotes"
          />
        </LoadingWrapper>
      </Grid>
      <Grid item xs={12} style={{ marginTop: 10 }}>
        <Paper style={{ padding: 16 }}>
          <p>
            <strong>Location:</strong> {xoiJob?.job_location}
          </p>
          <p>
            <strong>Completed:</strong> {xoiJob?.completed_at_with_time}
          </p>
        </Paper>
      </Grid>
      <Grid item xs={12} style={{ marginTop: 10 }}>
        <Paper style={{ padding: 16 }}>
          <h4>Servman Queries ({xoiJob?.spot_quote_sql_logs?.length || 0})</h4>
          {xoiJob?.spot_quote_sql_logs?.map((log: SpotQuoteSqlLog, index: number) => (
            <Box key={log.id}>
              {index > 0 && <Divider style={{ margin: '24px 0 16px' }} />}
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Typography variant="subtitle2">{log.category}</Typography>
                <Chip label={log.status || 'pending'} size="small" color={statusColor(log.status)} />
                <Typography variant="caption" color="textSecondary">
                  {log.created_at_with_time}
                </Typography>
              </Box>
              {log.fields ? (
                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 24, rowGap: 6 }}>
                  {log.fields.map((field) => (
                    <Box key={field.column} style={{ display: 'flex', gap: 6, minWidth: 0 }}>
                      <Typography variant="body2" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {field.column}:
                      </Typography>
                      <Typography variant="body2" style={{ wordBreak: 'break-word' }}>
                        {field.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.query}</pre>
              )}
            </Box>
          ))}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SpotQuoteDetailPage;
